/**
 * `/gallery/box-score` exists so `tests/e2e/src/app/boxScoreGeometry.e2e.ts`
 * can measure the Box score table's frozen player column in a real browser
 * (ISSUE-990 D3). It is signed in and linked from no navigation, following
 * the `/gallery/week-strip` precedent.
 *
 * The file is named `gallery_.box-score` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { BoxScoreTable } from "../features/gameDetail/BoxScoreTable";
import type {
  typeBoxScoreTable,
  typeBoxScoreTeamInfo,
} from "../features/gameDetail/boxScoreView";

const BASKETBALL_TEAM: typeBoxScoreTeamInfo = {
  teamId: "gallery-team",
  name: "Knicks",
  abbreviation: "NYK",
};

const FOOTBALL_TEAM: typeBoxScoreTeamInfo = {
  teamId: "gallery-team-2",
  name: "Chiefs",
  abbreviation: "KC",
};

const BASKETBALL_COLUMNS = [
  { key: "minutes", label: "MIN" },
  { key: "points", label: "PTS" },
  { key: "fieldGoals", label: "FG" },
  { key: "threes", label: "3PT" },
  { key: "freeThrows", label: "FT" },
  { key: "rebounds", label: "REB" },
  { key: "assists", label: "AST" },
  { key: "steals", label: "STL" },
  { key: "blocks", label: "BLK" },
  { key: "turnovers", label: "TO" },
  { key: "fouls", label: "PF" },
  { key: "plusMinus", label: "+/-" },
];

const basketballCells = (values: readonly string[]) =>
  BASKETBALL_COLUMNS.map((column, index) => ({
    key: column.key,
    display: values[index] ?? "0",
  }));

const BASKETBALL_TABLE: typeBoxScoreTable = {
  key: "players",
  label: "Players",
  columns: BASKETBALL_COLUMNS,
  rows: [
    {
      playerId: "gallery-player-1",
      name: "Jalen Brunson",
      position: "G",
      headshotUrl: null,
      cells: basketballCells([
        "36",
        "31",
        "11-22",
        "3-8",
        "6-7",
        "4",
        "8",
        "1",
        "0",
        "2",
        "2",
        "+9",
      ]),
    },
    {
      playerId: "gallery-player-2",
      name: "Karl-Anthony Towns",
      position: "F",
      headshotUrl: null,
      cells: basketballCells([
        "34",
        "24",
        "9-17",
        "2-5",
        "4-4",
        "13",
        "3",
        "0",
        "2",
        "3",
        "4",
        "+5",
      ]),
    },
    {
      playerId: "gallery-player-3",
      name: "Mikal Bridges",
      position: "F",
      headshotUrl: null,
      cells: basketballCells([
        "38",
        "17",
        "7-14",
        "3-6",
        "0-0",
        "5",
        "4",
        "2",
        "1",
        "1",
        "1",
        "-2",
      ]),
    },
  ],
  totals: [
    { key: "points", display: "72" },
    { key: "fieldGoals", display: "27-53" },
    { key: "threes", display: "8-19" },
    { key: "freeThrows", display: "10-11" },
    { key: "rebounds", display: "22" },
    { key: "assists", display: "15" },
    { key: "steals", display: "3" },
    { key: "blocks", display: "3" },
    { key: "turnovers", display: "6" },
    { key: "fouls", display: "7" },
  ],
};

const FOOTBALL_TABLE: typeBoxScoreTable = {
  key: "passing",
  label: "Passing",
  columns: [
    { key: "compAtt", label: "C/ATT" },
    { key: "yards", label: "YDS" },
    { key: "average", label: "AVG" },
    { key: "touchdowns", label: "TD" },
    { key: "interceptions", label: "INT" },
    { key: "rating", label: "RTG" },
  ],
  rows: [
    {
      playerId: "gallery-player-4",
      name: "Patrick Mahomes",
      position: "QB",
      headshotUrl: null,
      cells: [
        { key: "compAtt", display: "24/35" },
        { key: "yards", display: "291" },
        { key: "average", display: "8.3" },
        { key: "touchdowns", display: "2" },
        { key: "interceptions", display: "1" },
        { key: "rating", display: "104.2" },
      ],
    },
    {
      playerId: "gallery-player-5",
      name: "Carson Wentz",
      position: "QB",
      headshotUrl: null,
      cells: [
        { key: "compAtt", display: "2/3" },
        { key: "yards", display: "18" },
        { key: "average", display: "6.0" },
        { key: "touchdowns", display: "0" },
        { key: "interceptions", display: "0" },
      ],
    },
    {
      playerId: "gallery-player-6",
      name: "Christopher Montgomery-Wallace",
      position: "QB",
      headshotUrl: null,
      cells: [
        { key: "compAtt", display: "0/0" },
        { key: "yards", display: "0" },
        { key: "average", display: "0.0" },
        { key: "touchdowns", display: "0" },
        { key: "interceptions", display: "0" },
      ],
    },
  ],
  totals: [
    { key: "compAtt", display: "26/38" },
    { key: "yards", display: "309" },
    { key: "average", display: "8.1" },
    { key: "touchdowns", display: "2" },
    { key: "interceptions", display: "1" },
  ],
};

const BoxScoreGalleryScreen = (): ReactNode => (
  <div
    data-testid="box-score-gallery"
    className="flex flex-col gap-6 px-page-gutter py-4"
  >
    <h1 className="text-h1 text-ink">Box score gallery</h1>
    <p className="text-body text-ink-secondary">
      The box score table at phone width. This page exists so a browser can
      measure the frozen player column. It is not linked from any navigation.
    </p>
    <div data-testid="box-score-gallery-basketball">
      <BoxScoreTable table={BASKETBALL_TABLE} team={BASKETBALL_TEAM} />
    </div>
    <div data-testid="box-score-gallery-football">
      <BoxScoreTable table={FOOTBALL_TABLE} team={FOOTBALL_TEAM} />
    </div>
  </div>
);

export const Route = createFileRoute("/gallery_/box-score")({
  beforeLoad: requireSignedIn,
  component: BoxScoreGalleryScreen,
});
